import { Camera, Image, ImageUp, RotateCw } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router'
import { Button } from '@/components/ui/button'
import { StatusBadge } from '@/components/ui/status-badge'
import { Surface } from '@/components/ui/surface'
import { openSignedDocument } from '@/features/preinvest/documents/openSignedDocument'
import { formatDatumTijd } from '@/lib/format'
import { toastFailure } from '@/lib/feedback'
import { toast } from '@/lib/toast'
import { getScanPhotoUrl, listListScans, retryScan, uploadListScan } from '../api'
import type { CrmCardScan } from '../types'

/** Poll cadence while scans are pending or processing. */
const POLL_MS = 3000

const isActive = (scan: CrmCardScan): boolean => scan.status === 'pending' || scan.status === 'processing'

/**
 * The photo opens in a new tab through `openSignedDocument`, which opens the tab before the await so that
 * the pop-up blocker of iOS Safari does not lose the click while the signed URL is fetched.
 */
async function openPhoto(scanId: string) {
  try {
    await openSignedDocument(() => getScanPhotoUrl(scanId).then((answer) => answer.url))
  } catch {
    toast.error('Kon de foto niet openen.')
  }
}

/**
 * Business cards on a static list (spec-63 E5, ED-41), the scan screen of the old event page: a big scan
 * button (camera on a phone), an upload for existing photos, and the scans with their status. While scans are
 * being processed the card polls every three seconds; a scan that turns out to have made a contact tells the
 * page, so the members are read again. A failed scan can be retried and its photo opened.
 */
export function CardScansCard({ listId, archived, onCompleted }: { listId: string; archived: boolean; onCompleted: () => void }) {
  const [scans, setScans] = useState<CrmCardScan[] | null>(null)
  const [failed, setFailed] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [uploadErrors, setUploadErrors] = useState<string[]>([])
  const [retrying, setRetrying] = useState<ReadonlySet<string>>(new Set())
  const cameraInput = useRef<HTMLInputElement>(null)
  const uploadInput = useRef<HTMLInputElement>(null)
  /** The completed scans as of the last answer: a scan that completes now is news for the members. */
  const completed = useRef<ReadonlySet<string> | null>(null)
  const told = useRef(onCompleted)
  useEffect(() => {
    told.current = onCompleted
  })

  const take = useCallback((next: CrmCardScan[]) => {
    const done = new Set(next.filter((scan) => scan.status === 'completed').map((scan) => scan.id))
    const news = completed.current !== null && [...done].some((id) => !completed.current?.has(id))
    completed.current = done
    setScans(next)
    if (news) told.current()
  }, [])

  const reload = useCallback(async () => {
    try {
      take(await listListScans(listId))
      setFailed(false)
    } catch {
      setFailed(true)
    }
  }, [listId, take])

  useEffect(() => {
    let stale = false
    listListScans(listId).then(
      (found) => {
        if (stale) return
        take(found)
        setFailed(false)
      },
      () => {
        if (!stale) setFailed(true)
      },
    )
    return () => {
      stale = true
    }
  }, [listId, take])

  const active = scans?.some(isActive) ?? false
  // A chain of timeouts, each planned after the answer of the previous one: a slow answer can never overlap the next.
  useEffect(() => {
    if (!active) return
    let stale = false
    let timer: number | undefined
    async function tick() {
      try {
        const next = await listListScans(listId)
        if (!stale) take(next)
      } catch {
        // A hiccup: the next tick tries again.
      }
      if (!stale) timer = window.setTimeout(() => void tick(), POLL_MS)
    }
    timer = window.setTimeout(() => void tick(), POLL_MS)
    return () => {
      stale = true
      if (timer !== undefined) window.clearTimeout(timer)
    }
  }, [active, listId, take])

  /** One upload per chosen file; the files that fail are listed. The input is emptied first, so the same file can be chosen again. */
  async function handleFiles(input: HTMLInputElement) {
    const files = input.files ? [...input.files] : []
    input.value = ''
    if (files.length === 0 || uploading) return
    setUploading(true)
    setUploadErrors([])
    const errors: string[] = []
    let uploaded = 0
    for (const file of files) {
      try {
        await uploadListScan(listId, file)
        uploaded += 1
      } catch (caught) {
        errors.push(`${file.name}: ${caught instanceof Error ? caught.message : 'upload mislukt'}`)
      }
    }
    setUploading(false)
    setUploadErrors(errors)
    if (uploaded > 0) {
      toast.success(uploaded === 1 ? 'Foto geüpload; de scan wordt verwerkt.' : `${uploaded} foto's geüpload; de scans worden verwerkt.`)
      await reload()
    }
  }

  async function retry(scanId: string) {
    if (retrying.has(scanId)) return
    setRetrying((current) => new Set(current).add(scanId))
    try {
      const updated = await retryScan(scanId)
      setScans((current) => current?.map((scan) => (scan.id === updated.id ? updated : scan)) ?? current)
    } catch (caught) {
      toastFailure(caught, 'Opnieuw proberen mislukt.')
    } finally {
      setRetrying((current) => {
        const next = new Set(current)
        next.delete(scanId)
        return next
      })
    }
  }

  const blocked = archived || uploading
  return (
    <Surface variant="page" className="flex flex-col gap-3">
      <h2 className="font-heading text-lg font-semibold">{scans === null ? 'Visitekaartjes' : `Visitekaartjes (${scans.length})`}</h2>
      {archived ? <p className="text-sm text-muted-foreground">Deze lijst is gearchiveerd. Zet haar terug om weer visitekaartjes te scannen.</p> : null}
      <div className="flex flex-col gap-2">
        <Button type="button" size="lg" disabled={blocked} onClick={() => cameraInput.current?.click()}>
          <Camera />
          {uploading ? 'Bezig met uploaden…' : 'Scan visitekaartje'}
        </Button>
        <Button type="button" size="lg" variant="outline" disabled={blocked} onClick={() => uploadInput.current?.click()}>
          <ImageUp />
          Foto's uploaden
        </Button>
      </div>
      <input
        ref={cameraInput}
        type="file"
        accept="image/*"
        capture="environment"
        multiple
        className="hidden"
        aria-label="Visitekaartjes scannen"
        onChange={(event) => void handleFiles(event.currentTarget)}
      />
      <input
        ref={uploadInput}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        aria-label="Foto's kiezen"
        onChange={(event) => void handleFiles(event.currentTarget)}
      />
      {uploadErrors.length > 0 ? (
        <ul role="alert" className="flex flex-col gap-1 text-sm text-destructive">
          {uploadErrors.map((message, index) => (
            <li key={`${index}-${message}`}>{`Niet geüpload: ${message}`}</li>
          ))}
        </ul>
      ) : null}
      {failed && scans === null ? (
        <p role="alert" className="text-sm text-destructive">
          De scans laden mislukt.
        </p>
      ) : scans !== null && scans.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nog geen gescande visitekaartjes.</p>
      ) : (
        <ul className="flex flex-col">
          {(scans ?? []).map((scan) => (
            <li key={scan.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border py-3 first:pt-0 last:border-b-0 last:pb-0">
              <span className="text-sm text-muted-foreground">{formatDatumTijd(scan.created_at)}</span>
              {isActive(scan) ? (
                <StatusBadge tone="info">Verwerken…</StatusBadge>
              ) : scan.status === 'completed' ? (
                <>
                  <StatusBadge tone="success">Klaar</StatusBadge>
                  {scan.person ? (
                    <Link to={`/relaties/contacten/${scan.person.person_id}`} className="text-sm font-medium underline-offset-2 hover:underline">
                      {scan.person.full_name}
                    </Link>
                  ) : null}
                </>
              ) : (
                <>
                  <StatusBadge tone="danger" calm>
                    Mislukt
                  </StatusBadge>
                  <span className="text-sm text-destructive">{scan.error ?? 'Onbekende fout.'}</span>
                  <span className="flex items-center gap-2">
                    <Button type="button" variant="outline" size="sm" disabled={retrying.has(scan.id) || archived} onClick={() => void retry(scan.id)}>
                      <RotateCw />
                      Opnieuw
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => void openPhoto(scan.id)}>
                      <Image />
                      Foto
                    </Button>
                  </span>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
    </Surface>
  )
}
